'use client';

/* ============================================================================
 * lazyScene — client-only mounting wrapper for decorative product scenes
 * ============================================================================
 *
 * Why this file exists
 * --------------------
 * Every "scene" component in `src/components/v2/*Scene*.js` and
 * `src/components/v2/PillarScenes.js` is a Linear-style live UI mockup
 * (calendar grids, channel managers, AI inboxes, payment flows). Each
 * one is hundreds of DOM nodes deep with inline styling.
 *
 * Rendering them server-side blew the SSR HTML up to ~1 MB raw / 244
 * KiB gzipped per page (audit: docs/lighthouse-report.md, May 2026).
 * On simulated mobile networks the document download alone consumed
 * ~1.3 s of the LCP budget — the dominant cost on every landing page.
 *
 * What this helper does
 * ---------------------
 *   1. `next/dynamic` with `ssr: false` so the heavy markup never
 *      ships in the SSR HTML. Crawlers / SEO tools see a single
 *      placeholder element where the scene used to be (the scenes
 *      have no SEO value — they're product mockups, not content).
 *   2. The `loading` placeholder uses the same `ui-shell w-full`
 *      surface treatment as the real scene with a fixed `min-height`
 *      so the layout is reserved before the chunk loads. Keeps CLS
 *      at 0.000 across hydration.
 *   3. Each call site keeps its current import / JSX usage unchanged
 *      because every scene file re-exports the lazy component under
 *      the same name.
 *
 * Usage
 * -----
 *   // Default export
 *   const HeroPmsScene = lazyScene(() => import('./HeroPmsScene.impl'), {minH: 540});
 *   export default HeroPmsScene;
 *
 *   // Named export (resolves the named export inside the impl module)
 *   export const PmsScene = lazyScene(
 *     () => import('./PillarScenes.impl').then((m) => ({default: m.PmsScene})),
 *     {minH: 480},
 *   );
 *
 * Placeholder sizing
 * ------------------
 * Pass `aspect: '4 / 3'` for figures that already live inside an
 * aspect-ratio box (the parent constrains height anyway). Pass
 * `minH: <px>` for free-flowing scenes; choose a value close to the
 * scene's typical rendered height to minimise the visual shift when
 * the chunk lands. Erring slightly larger is fine — the page collapses
 * down on mount, which is unnoticeable below the fold.
 *
 * Constraints
 * -----------
 * `next/dynamic` with `ssr: false` is only legal inside client
 * components — that's why every scene wrapper (and this helper)
 * starts with `'use client'`. All current call sites are themselves
 * `'use client'` page components, so this is fine.
 * ========================================================================= */

import dynamic from 'next/dynamic';
import React from 'react';

export function lazyScene(loader, {minH = 460, aspect = null, className = 'ui-shell w-full'} = {}) {
  const placeholderStyle = aspect
    ? {aspectRatio: aspect}
    : {minHeight: minH};

  function ScenePlaceholder() {
    return (
      <div
        className={className}
        style={placeholderStyle}
        aria-hidden="true"
        role="presentation"
      />
    );
  }

  return dynamic(loader, {
    ssr: false,
    loading: ScenePlaceholder,
  });
}
